import { Divider, Stack } from '@mui/material';
import { ReactElement } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { LiveDataAttributesColumnQueryGuarded } from '../../../Prediction/AttributesColumn/AttributesColumn';
import { LiveDataMaterialColumnQueryGuarded } from '../../../Prediction/MaterialColumn/MaterialColumn';
import { PredictionColumnsLayout } from '../../../Prediction/PredictionColumnsLayout';
import { PredictionColoringTooltip } from '../../../Prediction/PredictionColoringTooltip';
import { AttributeChipData } from '../../../Prediction/types';
import { WrapperCardWithTitle } from '../../../../layouts/WrapperCardWithTitle';
import { MaterialClassification } from '../../../../api/queries/materialClassification';
import { ApiError } from '../../../ApiLoadingGuard/ApiLoadingGuard';
import { ResourceStatus } from '../../../../api/resources/useOcInsightsEvents';
import { PredictionWithApproval } from '../materialDecision';
import { ApprovalForm, Option } from './ApprovalForm';
import ProcessingTimeoutAlert from './ProcessingTimeoutAlert';

interface OcInsightsPredictionAreaProps {
  isNoImageCorrect: boolean;
  materialType: UseQueryResult<MaterialClassification, ApiError>;
  effectiveMaterialTypeStatus: ResourceStatus;
  edgeSize: UseQueryResult<AttributeChipData[], ApiError>;
  effectiveEdgeSizeStatus: ResourceStatus;
  soilClassification: UseQueryResult<AttributeChipData[], ApiError>;
  effectiveSoilClassificationStatus: ResourceStatus;
  showMaterialApproval: boolean;
  selectionOnly: boolean;
  materialApproval: PredictionWithApproval;
  materialOptions: Option[];
  selectedMaterialOption?: string;
  isMaterialPredictionTimedOut: boolean;
  onMaterialApproval: (isApproved: boolean) => void;
  onMaterialOptionSelect: (selectedValue: string) => void;
}

export function OcInsightsPredictionArea({
  isNoImageCorrect,
  materialType,
  effectiveMaterialTypeStatus,
  edgeSize,
  effectiveEdgeSizeStatus,
  soilClassification,
  effectiveSoilClassificationStatus,
  showMaterialApproval,
  selectionOnly,
  materialApproval,
  materialOptions,
  selectedMaterialOption,
  isMaterialPredictionTimedOut,
  onMaterialApproval,
  onMaterialOptionSelect,
}: OcInsightsPredictionAreaProps): ReactElement {
  return (
    <>
      <Divider />
      <WrapperCardWithTitle
        title='prediction.title'
        elevation={0}
        sx={{
          flex: 1,
          minHeight: '450px',
          display: 'flex',
          flexDirection: 'column',
        }}
        adornment={<PredictionColoringTooltip />}
      >
        <Stack flex={1}>
          {!isNoImageCorrect && (
            <Stack flex={1}>
              <PredictionColumnsLayout
                materialTypeColumn={
                  <LiveDataMaterialColumnQueryGuarded
                    materialClassificationQuery={materialType}
                    resourceStatus={effectiveMaterialTypeStatus}
                  />
                }
                attributesColumn={
                  <LiveDataAttributesColumnQueryGuarded
                    attributes={[
                      { resourceStatus: effectiveEdgeSizeStatus, query: edgeSize },
                      { resourceStatus: effectiveSoilClassificationStatus, query: soilClassification },
                    ]}
                    isMaterialClassificationPending={materialType.isPending}
                  />
                }
              />
            </Stack>
          )}
          {isMaterialPredictionTimedOut && <ProcessingTimeoutAlert />}
          {showMaterialApproval && (
            <ApprovalForm
              title={'selfService.materialSelection'}
              isApproved={materialApproval.isApproved}
              onApprovalChange={onMaterialApproval}
              options={materialOptions}
              selectedOption={selectedMaterialOption}
              onOptionSelect={onMaterialOptionSelect}
              selectionOnly={selectionOnly}
            />
          )}
        </Stack>
      </WrapperCardWithTitle>
    </>
  );
}
